// src/pages/MFADevice.js

import React, { useEffect, useMemo, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import ReusableTable from "../components/Table/ReusableTable";
import TableFilterBar from "../components/Table/TableFilterBar";
import ReusableHeader from "../components/Header/ReusableHeader";
import { request } from "../utils/request";
import { ColorModeContext } from "../index";
import { hasFeatureAccess, FEATURES } from "../utils/rbac";
import AccessDenied from "../components/guards/AccessDenied";

import {
  Typography,
  Stack,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import SmartphoneIcon from "@mui/icons-material/Smartphone";

/* =========================================================
   mEDGE DESIGN TOKENS
========================================================= */

const LIGHT_VARS = {
  "--vault-navy": "#0B2545",
  "--edge-blue": "#1F6FEB",
  "--canvas": "#FFFFFF",
  "--mist": "#F7F9FC",
  "--hairline": "#E4E9F0",
  "--blue-tint": "#EAF2FE",
  "--slate": "#6B7A90",
  "--secure": "#00A88F",
  "--high": "#E2622B",
  "--elevated": "#F0A202",
  "--critical": "#D6323A",
  "--border-radius": "6px",
};

const DARK_VARS = {
  "--vault-navy": "#F4F7FB",
  "--edge-blue": "#4C9AFF",
  "--canvas": "#0F1729",
  "--mist": "#252b3b",
  "--hairline": "rgba(255,255,255,0.14)",
  "--blue-tint": "rgba(76,154,255,0.16)",
  "--slate": "#93A4BD",
  "--secure": "#2FD3B8",
  "--high": "#F07C46",
  "--elevated": "#FFC24B",
  "--critical": "#FF6B72",
  "--border-radius": "6px",
};

// ReusableTable brings its own isolated styles.

/* =========================================================
   HELPERS
========================================================= */

const DEVICE_EDIT_PERMISSION = "MFA Control-Devices-DevicesUpdate";

const getStoredUser = () => {
  try {
    const raw = typeof window !== 'undefined' ? (localStorage.getItem("user") || sessionStorage.getItem("user")) : null;
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const extractErrorMessage = (err) => {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.error && typeof err.error === "string") return err.error;
  if (err?.message && typeof err.message === "string") return err.message;
  if (err?.response?.data?.error && typeof err.response.data.error === "string") return err.response.data.error;
  if (err?.response?.data?.message && typeof err.response.data.message === "string") return err.response.data.message;
  return "Could not update device status.";
};

/* =========================================================
   COMPONENT
========================================================= */

const MFADevice = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialDeviceStatus = () => {
    const s = searchParams.get("status");
    if (s === "blocked_or_revoked" || s === "blocked" || s === "revoked") return "Blocked / Revoked";
    if (s === "stale" || s === "inactive") return "Stale (>30d)";
    if (s === "active") return "Active";
    return "";
  };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({
    platform: searchParams.get("platform") || "",
    status: getInitialDeviceStatus(),
  });
  const [actionMenu, setActionMenu] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [updatingDeviceId, setUpdatingDeviceId] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);

  // Action reason state & inline validation
  const [actionReason, setActionReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);

  const { mode } = useContext(ColorModeContext || { mode: "light" });
  const V = mode === "dark" ? DARK_VARS : LIGHT_VARS;

  /* =====================================================
     PERMISSIONS
     Organization owner or user holding Devices edit permission
  ===================================================== */

  const currentUser = getStoredUser();
  const canRead = hasFeatureAccess(currentUser, FEATURES.DEVICES, 'read');
  const canEdit = hasFeatureAccess(currentUser, FEATURES.DEVICES, 'update');

  /* =====================================================
     FILTERS + SEARCH
  ===================================================== */

  useEffect(() => {
    const s = searchParams.get("status");
    const resolvedStatus =
      s === "blocked_or_revoked" || s === "blocked" || s === "revoked"
        ? "Blocked / Revoked"
        : s === "stale" || s === "inactive"
          ? "Stale (>30d)"
          : s === "active"
            ? "Active"
            : "";
    if (s !== null) {
      setFilterValues((prev) => (prev.status !== resolvedStatus ? { ...prev, status: resolvedStatus } : prev));
    }
  }, [searchParams]);

  const platformOptions = useMemo(() => {
    const platforms = new Set();
    data.forEach((d) => {
      if (d.platform && d.platform !== "—") platforms.add(d.platform);
    });
    return Array.from(platforms).sort();
  }, [data]);

  const statusOptions = ["Active", "Blocked / Revoked", "Stale (>30d)"];

  const filters = [
    {
      id: "platform",
      label: "Platform",
      options: platformOptions,
      value: filterValues.platform,
    },
    {
      id: "status",
      label: "Device Status",
      options: statusOptions,
      value: filterValues.status,
    },
  ];

  const handleFilterChange = (id, value) => {
    setFilterValues((prev) => ({ ...prev, [id]: value }));
    setSearchParams((prevParams) => {
      const next = new URLSearchParams(prevParams);
      if (id === "status") {
        if (value === "Blocked / Revoked") next.set("status", "blocked_or_revoked");
        else if (value === "Stale (>30d)") next.set("status", "stale");
        else if (value === "Active") next.set("status", "active");
        else next.delete("status");
      } else if (value) {
        next.set(id, value);
      } else {
        next.delete(id);
      }
      return next;
    }, { replace: true });
  };

  const filteredData = useMemo(() => {
    let result = data;

    if (filterValues.platform) {
      result = result.filter((item) => item.platform === filterValues.platform);
    }

    if (filterValues.status) {
      if (filterValues.status === "Blocked / Revoked" || filterValues.status === "blocked_or_revoked") {
        result = result.filter((item) => item.isBlocked === true || item.revokedAt != null);
      } else if (filterValues.status === "Stale (>30d)" || filterValues.status === "stale") {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        result = result.filter((item) => !item.lastSeenAt || new Date(item.lastSeenAt) < thirtyDaysAgo);
      } else if (filterValues.status === "Active" || filterValues.status === "active") {
        result = result.filter((item) => !item.isBlocked && item.revokedAt == null);
      }
    }

    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      result = result.filter((item) =>
        [item.userName, item.userEmail, item.deviceName, item.platform].some(
          (val) => String(val ?? "").toLowerCase().includes(lower)
        )
      );
    }

    return result;
  }, [data, searchQuery, filterValues]);

  /* =====================================================
     DEVICE ACTIONS
  ===================================================== */

  const actionMenuDevice = actionMenu?.deviceId
    ? data.find((device) => String(device._id) === actionMenu.deviceId)
    : null;

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  const getRegistrationStatus = (device) => {
    if (device?.revokedAt != null) return "Revoked";
    if (device?.isBlocked === true) return "Blocked";
    return "Active";
  };

  const validateReason = (value, action) => {
    const isRequired = action === "block" || action === "revoke";
    const trimmed = (value || "").trim();
    if (isRequired) {
      if (!trimmed) {
        return "Reason is required (3 to 500 characters).";
      }
      if (trimmed.length < 3) {
        return "Reason must be at least 3 characters.";
      }
      if (trimmed.length > 500) {
        return "Reason cannot exceed 500 characters.";
      }
      return "";
    }
    // Optional for unblock and unrevoke
    if (trimmed.length > 500) {
      return "Reason cannot exceed 500 characters.";
    }
    return "";
  };

  const openActionDialog = (actionConfig) => {
    setActionReason("");
    setReasonError("");
    setReasonTouched(false);
    setConfirmAction(actionConfig);
    setActionMenu(null);
  };

  const confirmDeviceAction = async () => {
    if (!confirmAction?.device?._id) return;

    const validationErr = validateReason(actionReason, confirmAction.action);
    if (validationErr) {
      setReasonTouched(true);
      setReasonError(validationErr);
      return;
    }

    const device = confirmAction.device;
    const action = confirmAction.action;
    const shouldBlock = action === "block";
    const shouldRevoke = action === "revoke";
    const shouldUnrevoke = action === "unrevoke";
    const deviceId = String(device._id);
    setUpdatingDeviceId(deviceId);

    try {
      const trimmedReason = actionReason.trim();
      const body = trimmedReason ? { reason: trimmedReason } : {};

      const response = await request("devices", {
        method: "patch",
        params: `${deviceId}/${shouldRevoke ? "revoke" : shouldUnrevoke ? "unrevoke" : action}`,
        body,
      });
      const updatedDevice = response?.data;

      if (
        !response?.success ||
        String(updatedDevice?._id) !== deviceId ||
        (shouldRevoke
          ? updatedDevice?.revokedAt == null
          : shouldUnrevoke
            ? updatedDevice?.revokedAt != null
            : Boolean(updatedDevice?.isBlocked) !== shouldBlock)
      ) {
        throw new Error("The server did not confirm the device action.");
      }

      setData((current) =>
        current.map((item) =>
          String(item._id) === deviceId ? { ...item, ...updatedDevice } : item
        )
      );
      setActionFeedback({
        severity: "success",
        message: shouldRevoke
          ? "Device revoked successfully."
          : shouldUnrevoke
            ? "Device restored successfully."
            : `Device ${shouldBlock ? "blocked" : "unblocked"} successfully.`,
      });
      setConfirmAction(null);
    } catch (error) {
      setActionFeedback({
        severity: "error",
        message: extractErrorMessage(error),
      });
      setConfirmAction(null);
    } finally {
      setUpdatingDeviceId(null);
    }
  };

  const requestBlockAction = () => {
    if (!canEdit || !actionMenuDevice || actionMenuDevice.revokedAt != null) return;
    openActionDialog({
      device: actionMenuDevice,
      action: actionMenuDevice.isBlocked === true ? "unblock" : "block",
      shouldBlock: actionMenuDevice.isBlocked !== true,
    });
  };

  const requestRevokeAction = () => {
    if (!canEdit || !actionMenuDevice || actionMenuDevice.revokedAt != null) return;
    openActionDialog({
      device: actionMenuDevice,
      action: "revoke",
      shouldBlock: false,
    });
  };

  const requestUnrevokeAction = () => {
    if (!canEdit || !actionMenuDevice || actionMenuDevice.revokedAt == null) return;
    openActionDialog({
      device: actionMenuDevice,
      action: "unrevoke",
      shouldBlock: false,
    });
  };

  /* =====================================================
     TABLE COLUMNS
  ===================================================== */

  const columns = useMemo(
    () => [
      {
        id: "userName",
        label: "User Name",
        minWidth: 160,
        render: (row) => (
          <Typography
            variant="body2"
            noWrap
            title={row.userName}
            sx={{ fontWeight: 500, color: "var(--vault-navy)" }}
          >
            {row.userName}
          </Typography>
        ),
      },
      {
        id: "userEmail",
        label: "User Email",
        minWidth: 220,
        render: (row) => (
          <Typography
            variant="body2"
            noWrap
            title={row.userEmail}
            sx={{ color: "var(--slate)" }}
          >
            {row.userEmail}
          </Typography>
        ),
      },
      {
        id: "deviceName",
        label: "Device Name",
        minWidth: 200,
        render: (row) => (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--blue-tint)",
                color: "var(--edge-blue)",
              }}
            >
              <SmartphoneIcon fontSize="small" />
            </Box>
            <Typography variant="body2" noWrap title={row.deviceName}>
              {row.deviceName}
            </Typography>
          </Stack>
        ),
      },
      {
        id: "platform",
        label: "Platform",
        render: (row) => {
          const p = String(row.platform).toLowerCase();
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {p === "ios" || p === "apple" ? (
                <AppleIcon sx={{ color: "#555" }} fontSize="small" />
              ) : p === "android" ? (
                <AndroidIcon sx={{ color: "#3DDC84" }} fontSize="small" />
              ) : null}
              <Typography variant="body2">{row.platform}</Typography>
            </Stack>
          );
        },
      },
      {
        id: "lastSeenAt",
        label: "Last Seen",
        minWidth: 180,
        render: (row) => (
          <Typography variant="body2" sx={{ color: "var(--slate)" }}>
            {formatDate(row.lastSeenAt)}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        render: (row) => {
          if (!canEdit) {
            return <span style={{ color: "var(--slate)" }}>—</span>;
          }
          return (
            <Tooltip title="Device actions">
              <span>
                <IconButton
                  size="small"
                  aria-label={`Actions for ${row.deviceName || "device"}`}
                  aria-haspopup="menu"
                  onClick={(event) =>
                    setActionMenu({
                      anchorEl: event.currentTarget,
                      deviceId: String(row._id),
                    })
                  }
                  disabled={updatingDeviceId === String(row._id)}
                  sx={{
                    color: "var(--vault-navy)",
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "var(--blue-tint)",
                      color: "var(--edge-blue)",
                    },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [updatingDeviceId, canEdit]
  );

  /* =====================================================
     LOAD MFA DEVICES
  ===================================================== */

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);

      try {
        const [deviceResponse, userResponse] = await Promise.all([
          request("devices", {
            method: "get",
            query: { limit: 1000, skip: 0 },
          }),
          request("user", { method: "get" }).catch(() => []),
        ]);

        const devices = Array.isArray(deviceResponse?.data?.docs)
          ? deviceResponse.data.docs
          : Array.isArray(deviceResponse?.docs)
            ? deviceResponse.docs
            : Array.isArray(deviceResponse)
              ? deviceResponse
              : [];
        const users = Array.isArray(userResponse)
          ? userResponse
          : Array.isArray(userResponse?.data)
            ? userResponse.data
            : [];
        const usersById = new Map(
          users
            .filter((user) => user?._id != null)
            .map((user) => [String(user._id), user])
        );

        const mappedDevices = devices.map((device) => {
          const user = usersById.get(String(device?.userId));
          const userName = [user?.first_name, user?.last_name]
            .filter(Boolean)
            .map((name) => String(name).trim())
            .filter(Boolean)
            .join(" ");

          return {
            ...device,
            userName: userName || "—",
            userEmail: user?.email || "—",
            deviceName: device?.deviceName || "—",
            platform: device?.platform || "—",
          };
        });

        // Deduplicate devices by user + physical device
        const seenDeviceKeys = new Set();
        const deduplicated = [];
        for (const item of mappedDevices) {
          const uId = String(item.userId || "");
          const pForm = String(item.platform || "unknown").toLowerCase();
          const dName = String(item.deviceName || "").trim().toLowerCase();
          const key = uId && pForm && dName && dName !== "—"
            ? `${uId}:${pForm}:${dName}`
            : String(item._id || item.deviceId);
          if (!seenDeviceKeys.has(key)) {
            seenDeviceKeys.add(key);
            deduplicated.push(item);
          }
        }

        setData(deduplicated);
      } catch (err) {
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* =====================================================
     UI
  ===================================================== */

  const isReasonRequired =
    confirmAction?.action === "block" || confirmAction?.action === "revoke";

  if (!canRead) {
    return <AccessDenied featureName="Devices" title="Devices" />;
  }

  return (
    <React.Fragment>
      <Box
        className="page-content"
        style={{
          backgroundColor: "transparent",
          ...V,
        }}
      >
        <Container fluid>
          {/* PAGE HEADER */}
          <ReusableHeader
            title="MFA Devices"
            breadcrumbs={[{ label: "MFA Devices" }]}
          />

          {/* TABLE */}
          <Box sx={{ mt: 3 }}>
            <TableFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filters={filters}
              onFilterChange={handleFilterChange}
              searchPlaceholder="Search MFA Devices..."
            />
            <ReusableTable
              columns={columns}
              data={filteredData}
              loading={loading}
              emptyMessage="No MFA devices found."
            />
          </Box>

          {/* ACTION MENU */}
          <Menu
            anchorEl={actionMenu?.anchorEl || null}
            open={Boolean(actionMenu)}
            onClose={() => setActionMenu(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                minWidth: 190,
                maxWidth: 250,
                maxHeight: "min(320px, 60vh)",
                overflowY: "auto",
                color: V["--vault-navy"],
                backgroundColor: V["--canvas"],
                border: `1px solid ${V["--hairline"]}`,
                borderRadius: "6px",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.16)",
                "& .MuiMenuItem-root": { minHeight: 38, px: 1.5, fontSize: 14 },
              },
            }}
          >
            <MenuItem disabled sx={{ opacity: "1 !important", cursor: "default" }}>
              <ListItemIcon sx={{ minWidth: 32, color: V["--secure"] }}>
                <CheckCircleOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Registered"
                secondary={getRegistrationStatus(actionMenuDevice)}
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: V["--vault-navy"],
                }}
                secondaryTypographyProps={{ fontSize: 12, color: V["--slate"] }}
              />
            </MenuItem>
            {canEdit && <Divider sx={{ borderColor: V["--hairline"] }} />}
            {canEdit && actionMenuDevice?.revokedAt == null && (
              <MenuItem
                disabled={
                  !actionMenuDevice ||
                  updatingDeviceId === String(actionMenuDevice?._id)
                }
                onClick={requestBlockAction}
              >
                <ListItemIcon sx={{ minWidth: 32, color: V["--slate"] }}>
                  <BlockOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {actionMenuDevice?.isBlocked === true ? "Unblock" : "Block"}
              </MenuItem>
            )}
            {canEdit &&
              (actionMenuDevice?.revokedAt != null ? (
                <Tooltip title="Restore this device with its original key (biometrics must be re-enrolled)">
                  <span>
                    <MenuItem
                      disabled={
                        !actionMenuDevice ||
                        updatingDeviceId === String(actionMenuDevice?._id)
                      }
                      onClick={requestUnrevokeAction}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: V["--slate"] }}>
                        <RestoreIcon fontSize="small" />
                      </ListItemIcon>
                      Restore (same key)
                    </MenuItem>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Revoke this device so it can no longer authenticate">
                  <span>
                    <MenuItem
                      disabled={
                        !actionMenuDevice ||
                        updatingDeviceId === String(actionMenuDevice?._id)
                      }
                      onClick={requestRevokeAction}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: V["--slate"] }}>
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      Revoke
                    </MenuItem>
                  </span>
                </Tooltip>
              ))}
          </Menu>

          {/* CONFIRM DIALOG */}
          <Dialog
            open={Boolean(confirmAction)}
            onClose={() => {
              if (!updatingDeviceId) setConfirmAction(null);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              {confirmAction?.action === "revoke"
                ? "Revoke device?"
                : confirmAction?.action === "unrevoke"
                  ? "Restore device (same key)?"
                  : confirmAction?.shouldBlock
                    ? "Block device?"
                    : "Unblock device?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 1.5 }}>
                {confirmAction?.action === "revoke"
                  ? `Revoke ${confirmAction.device?.deviceName || "this device"}? It will no longer be usable for authentication, and its biometric credential will be permanently invalidated.`
                  : confirmAction?.action === "unrevoke"
                    ? `Restore ${confirmAction.device?.deviceName || "this device"} (${confirmAction.device?.deviceId || "unknown device ID"})? This clears the revocation state so the device can authenticate again.`
                    : confirmAction?.shouldBlock
                      ? `Block ${confirmAction.device?.deviceName || "this device"}? It will no longer authenticate or approve MFA Push challenges.`
                      : `Unblock ${confirmAction?.device?.deviceName || "this device"}? Other trust or revocation restrictions will remain in effect.`}
              </DialogContentText>

              {confirmAction?.action === "unrevoke" && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Warning:</strong> The original key becomes valid again and the biometric credential must be re-enrolled.
                </Alert>
              )}

              <TextField
                autoFocus
                margin="dense"
                id="action-reason"
                name="reason"
                label={isReasonRequired ? "Reason *" : "Reason (optional)"}
                placeholder={
                  isReasonRequired
                    ? "Enter reason for this action (min 3 characters)..."
                    : "Enter optional reason..."
                }
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={actionReason}
                onChange={(e) => {
                  const val = e.target.value;
                  setActionReason(val);
                  if (reasonTouched) {
                    setReasonError(validateReason(val, confirmAction?.action));
                  }
                }}
                onBlur={() => {
                  setReasonTouched(true);
                  setReasonError(validateReason(actionReason, confirmAction?.action));
                }}
                error={Boolean(reasonError)}
                helperText={
                  reasonError ||
                  `${actionReason.trim().length}/500 characters${isReasonRequired ? " (min 3)" : ""
                  }`
                }
                inputProps={{ maxLength: 500 }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setConfirmAction(null)}
                disabled={Boolean(updatingDeviceId)}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeviceAction}
                variant="contained"
                disabled={
                  Boolean(updatingDeviceId) ||
                  (isReasonRequired && actionReason.trim().length < 3) ||
                  actionReason.trim().length > 500
                }
                color={
                  confirmAction?.shouldBlock || confirmAction?.action === "revoke"
                    ? "error"
                    : "primary"
                }
                startIcon={
                  updatingDeviceId ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
              >
                {confirmAction?.action === "revoke"
                  ? "Revoke"
                  : confirmAction?.action === "unrevoke"
                    ? "Restore (same key)"
                    : confirmAction?.shouldBlock
                      ? "Block"
                      : "Unblock"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* FEEDBACK */}
          <Snackbar
            open={Boolean(actionFeedback)}
            autoHideDuration={4000}
            onClose={() => setActionFeedback(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              severity={actionFeedback?.severity || "info"}
              variant="filled"
              onClose={() => setActionFeedback(null)}
            >
              {actionFeedback?.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </React.Fragment>
  );
};

export default MFADevice;